import { useQuery } from '@apollo/client';
import { type Maybe } from 'graphql/jsutils/Maybe';
import { createContext, type ReactNode, useContext, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { Project } from '@gql/graphql';
import ErrorMessage from '@components/ErrorMessage';
import { GET_PROJECT_SUMMARY } from '@api/queries/projects';

export interface ProjectSummaryProviderProps {
    children?: ReactNode;
}

interface ProjectSummaryContextValue {
    project: Maybe<Project>;
    loading: boolean;
}

const ProjectSummaryContext = createContext<ProjectSummaryContextValue>({
    project: undefined,
    loading: false,
});

export function ProjectSummaryProvider({
    children,
}: ProjectSummaryProviderProps): ReactNode {
    const { pid } = useParams<{ pid: string }>();
    const navigate = useNavigate();

    if (!pid) {
        throw new Error('Project ID is required');
    }

    const { data, loading, error } = useQuery(GET_PROJECT_SUMMARY, {
        variables: { pid },
        fetchPolicy: 'cache-and-network',
        skip: !pid,
        onCompleted: (queryData) => {
            if (!queryData.projects?.edges[0]?.node) {
                navigate('/page-not-found');
                console.error(`Project with id: ${pid} not found`);
            }
        },
    });

    if (error) {
        return <ErrorMessage error={error} />;
    }

    const project = data?.projects?.edges[0]?.node as Project | undefined;

    const value = useMemo(
        () => ({
            project,
            loading,
        }),
        [project, loading],
    );

    return (
        <ProjectSummaryContext.Provider value={value}>
            {children}
        </ProjectSummaryContext.Provider>
    );
}

export function useProjectSummary(): ProjectSummaryContextValue {
    const context = useContext(ProjectSummaryContext);
    if (!context) {
        throw new Error(
            'useProjectSummary must be used within a ProjectSummaryContext',
        );
    }

    return context;
}
